import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import Swal from "sweetalert2";
import axios from "axios";
import "./login.css";
import { API_LoginURL, API_BannerURL } from "../../config"; // Added API_AdBanners
import { RotatingLines } from "react-loader-spinner";

export default function Login() {
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  const [banners, setBanners] = useState([]);
  const [bannerLoading, setBannerLoading] = useState(true);
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);
  // Fetch advertisement banners
  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const response = await axios.get(
          `${API_LoginURL}banner/BannerMasterList`
        );
        console.log("Fetched banners:", response.data);
        setBanners(response.data);
        console.log("Banners set to state:", banners);
      } catch (error) {
        console.error("Error fetching banners:", error);
        // Fallback banners if API fails
        // setBanners([
        //   {
        //     id: 1,
        //     position: 'top',
        //     imageUrl: '/assets/default-banner-top.jpg',
        //     redirectUrl: 'https://xroboticsworks.com/offers',
        //     altText: 'Special Offers'
        //   },
        //   {
        //     id: 2,
        //     position: 'left',
        //     imageUrl: '/assets/default-banner-left.jpg',
        //     redirectUrl: 'https://xroboticsworks.com/new-courses',
        //     altText: 'New Courses'
        //   },
        //   {
        //     id: 3,
        //     position: 'right',
        //     imageUrl: '/assets/default-banner-right.jpg',
        //     redirectUrl: 'https://xroboticsworks.com/events',
        //     altText: 'Upcoming Events'
        //   }
        // ]);
      } finally {
        setBannerLoading(false);
      }
    };

    fetchBanners();
  }, []);

  // Check if user is already logged in
  useEffect(() => {
    const token = localStorage.getItem("token");
    const role = localStorage.getItem("role");

    if (token && role) {
      const roleRedirects = {
        SuperAdmin: "/SuperAdminDashboard",
        Student: "/studentDashboard",
        Teacher: "/TeacherDashboard",
        Admin: "/adminDashboard",
      };

      if (role in roleRedirects) {
        navigate(roleRedirects[role]);
      }
    }
  }, [navigate]);

  const handleBannerClick = (url) => {
    window.open(url, "_blank");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!userId || !password) {
      Swal.fire({
        icon: "warning",
        title: "Missing Fields",
        text: "Please enter both User ID and Password",
      });
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(API_LoginURL + "user/Loggedin", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userName: userId,
          password: password,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Login failed");
      }

      const data = await response.json();

      localStorage.setItem("token", "Bearer " + data.token);
      localStorage.setItem("role", data.type);

      const roleRedirects = {
        SuperAdmin: "/SuperAdminDashboard",
        Student: "/studentDashboard",
        Teacher: "/TeacherDashboard",
        Admin: "/adminDashboard",
      };

      if (data.type in roleRedirects) {
        navigate(roleRedirects[data.type]);
      } else {
        throw new Error("Unknown user role");
      }
    } catch (error) {
      console.error("Login error:", error);
      Swal.fire({
        icon: "error",
        title: "Login Failed",
        text:
          error.message ||
          "An error occurred while trying to log in. Please try again later.",
      });
    } finally {
      setLoading(false);
    }
  };

  // Get banners by position
  const topBanner = banners.find((b) => b.bannerPageLocation === "Header");
  const leftBanner = banners.find(
    (b) => b.bannerPageLocation === "Left Sidebar"
  );
  const rightBanner = banners.find(
    (b) => b.bannerPageLocation === "Right Sidebar"
  );

  return (
    <div className="responsive-body">
      <div className="responsive-container">
        <header className="responsive-header">
          <div>
            <img
              className="responsive-logoimg"
              src="/assets/logo.png"
              alt="logo"
            />
          </div>
          <div className="contact-info">
            <p>CONTACT US:</p>
            <h4>info@xroboticsworks.com</h4>
          </div>
        </header>

        {/* Top Banner with animation  */}
        {topBanner && !bannerLoading && banners.bannerStatus && (
          <div
            className="top-banner animate__animated animate__fadeInDown animate__delay-1s"
            onClick={() => handleBannerClick(topBanner.bannerLink)}
          >
            <img
              src={API_BannerURL + topBanner.bannerPath}
              alt={topBanner.altText}
              className="banner-image pulse-animation"
            />
            <div className="banner-overlay">
              <span className="banner-text">{topBanner.altText}</span>
            </div>
          </div>
        )}

        <main className="responsive-main">
          {/* Left Side Banner with animation  */}
          {leftBanner && !bannerLoading && banners.bannerStatus && (
            <div
              className="side-banner left-banner animate__animated animate__fadeInLeft animate__delay-2s"
              onClick={() => handleBannerClick(leftBanner.bannerLink)}
            >
              <img
                src={API_BannerURL + leftBanner.bannerPath}
                alt={leftBanner.altText}
                className="banner-image float-animation"
              />
              <div className="banner-overlay">
                <span className="banner-text">{leftBanner.altText}</span>
                <span className="banner-cta">Click to learn more →</span>
              </div>
            </div>
          )}

          <div className="responsive-login-form">
            <h2>Log In</h2>
            <form onSubmit={handleSubmit}>
              <label htmlFor="user-id">USER ID</label>
              <input
                type="text"
                id="user-id"
                placeholder="Enter your User ID"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                required
                disabled={loading}
              />

              <label htmlFor="password">PASSWORD</label>

              <div className="password-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  placeholder="Enter your Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyUp={(e) => setCapsLockOn(e.getModifierState("CapsLock"))}
                  required
                  disabled={loading}
                  className="password-input"
                />

                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>

              {capsLockOn && (
                <small className="caps-warning">⚠️ Caps Lock is ON</small>
              )}
              <Link to="/forgotpassword">Forgot Password?</Link>

              <button type="submit" disabled={loading} className="btn-primary">
                {loading ? (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <RotatingLines
                      strokeColor="white"
                      strokeWidth="5"
                      animationDuration="0.75"
                      width="20"
                      visible={true}
                    />
                  </div>
                ) : (
                  "Sign In"
                )}
              </button>

              <button
                type="button"
                className="btn-secondary"
                disabled={loading}
              >
                <img
                  className="responsive-microsoft"
                  src="/assets/microsoft.png"
                  alt="Microsoft Logo"
                />{" "}
                &nbsp; Sign In With Microsoft
              </button>
            </form>
          </div>

          {/* Right Side Banner with animation  */}
          {rightBanner && !bannerLoading && banners.bannerStatus && (
            <div
              className="side-banner right-banner animate__animated animate__fadeInRight animate__delay-2s"
              onClick={() => handleBannerClick(rightBanner.bannerLink)}
            >
              <img
                src={API_BannerURL + rightBanner.bannerPath}
                alt={rightBanner.altText}
                className="banner-image float-animation"
              />
              <div className="banner-overlay">
                <span className="banner-text">{rightBanner.altText}</span>
                <span className="banner-cta">Click to learn more →</span>
              </div>
            </div>
          )}
        </main>

        <footer className="responsive-footer">
          <div className="website1">
            <p>VISIT OUR WEBSITE </p>
            <a href="https://xroboticsworks.com">xroboticsworks.com</a>
          </div>
          <div className="links">
            <a href="#">FOLLOW US</a>
          </div>
          <div className="social-icons">
            <a href="https://www.facebook.com">
              <img
                className="responsive-microsoft"
                src="/assets/facebook.png"
                alt="Facebook Logo"
              />
            </a>
            <a href="#">
              <img
                className="responsive-microsoft"
                src="/assets/Email.png"
                alt="Email Logo"
              />
            </a>
            <a href="#">
              <img
                className="responsive-microsoft"
                src="/assets/Telegram.png"
                alt="Telegram Logo"
              />
            </a>
            <a href="#">
              <img
                className="responsive-microsoft"
                src="/assets/whatsapp.png"
                alt="WhatsApp Logo"
              />
            </a>
            <a href="#">
              <img
                className="responsive-microsoft"
                src="/assets/snapchatt.png"
                alt="Snapchat Logo"
              />
            </a>
          </div>
          <div className="website2">
            <p>VISIT OUR WEBSITE </p>
            <a href="https://shop.xroboticsworks.com">
              shop.xroboticsworks.com
            </a>
          </div>
        </footer>
      </div>
    </div>
  );
}
